import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import { actionNotifier } from "../ui/toast";
import { PhotoPicker, UploadGuidelines, UploadHistory, selectStyles, MAX_PHOTOS } from "./PhotoUpload";

const today = () => moment().format("YYYY-MM-DD");

/**
 * The one photo-upload screen, shared by "Add School Document" and "Add VC
 * Document" so both stay identical: Block → item (searchable) → photo date →
 * photos (one or many) → Upload button, then the officer's own upload history.
 *
 * The page supplies only what differs:
 *   eyebrow, title, subtitle          page heading
 *   item: { label, labelHi, noun, plural }  what is being photographed ("School"/"school"/"schools")
 *   fixedBlock                        a BEO's own block (locks the block field)
 *   loadBlocks()                      -> Promise<string[]>
 *   loadItems(block)                  -> Promise<[{ value, label }]>
 *   upload({ item, file, date })      -> Promise<api response>, called once per photo
 *   fetchHistory(params)              -> Promise<api response>
 */
const PhotoUploadScreen = ({ eyebrow, title, subtitle, item, fixedBlock, loadBlocks, loadItems, upload, fetchHistory }) => {
    const [blocks, setBlocks] = useState([]);
    const [block, setBlock] = useState(fixedBlock || "");
    const [items, setItems] = useState([]);
    const [loadingBlocks, setLoadingBlocks] = useState(true);
    const [loadingItems, setLoadingItems] = useState(false);
    const [selected, setSelected] = useState(null);
    const [date, setDate] = useState(today());
    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(null);
    const [historyKey, setHistoryKey] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoadingBlocks(true);
        loadBlocks()
            .then((list) => alive && setBlocks(list || []))
            .finally(() => alive && setLoadingBlocks(false));
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        setSelected(null);
        setItems([]);
        if (!block) return undefined;
        setLoadingItems(true);
        loadItems(block)
            .then((list) => alive && setItems(list || []))
            .finally(() => alive && setLoadingItems(false));
        return () => { alive = false; };
    }, [block]);

    const addPhotos = (files, truncated) => {
        setPhotos((prev) => [
            // Drop photos from a finished batch before starting a new one.
            ...prev.filter((p) => p.status !== "done"),
            ...files.map((file, i) => ({
                id: `${Date.now()}-${i}-${file.name}`,
                file,
                preview: URL.createObjectURL(file),
                status: "ready",
                error: "",
            })),
        ]);
        if (truncated) actionNotifier.warning(`You can upload up to ${MAX_PHOTOS} photos at a time.`);
    };

    const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

    const pending = photos.filter((p) => p.status === "ready" || p.status === "failed");
    const setStatus = (id, status, error = "") =>
        setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, status, error } : p)));

    const ready = block && selected && date && pending.length > 0 && !submitting;
    const missing = !block
        ? "Select a block"
        : !selected
            ? `Select a ${item.noun}`
            : !date
                ? "Choose the photo date"
                : !pending.length
                    ? "Add one or more photos"
                    : "";

    // One request per photo (the server stores one row per photo), in sequence so
    // a failure affects only that photo. Failed photos stay for a retry.
    const submit = async (e) => {
        e.preventDefault();
        if (!ready) return;
        const batch = pending;
        setSubmitting(true);
        let ok = 0;
        let failed = 0;
        for (let i = 0; i < batch.length; i++) {
            const p = batch[i];
            setProgress({ current: i + 1, total: batch.length });
            setStatus(p.id, "uploading");
            try {
                const res = await upload({ item: selected, file: p.file, date });
                if (res?.status) {
                    ok += 1;
                    setStatus(p.id, "done");
                } else {
                    failed += 1;
                    setStatus(p.id, "failed", res?.message || "Upload failed");
                }
            } catch (err) {
                failed += 1;
                setStatus(p.id, "failed", "Upload failed");
            }
        }
        setProgress(null);
        setSubmitting(false);
        if (ok) setHistoryKey((k) => k + 1);
        if (!failed) {
            actionNotifier.success(ok === 1 ? "Photo uploaded successfully" : `${ok} photos uploaded successfully`);
            setSelected(null);
        } else if (ok) {
            actionNotifier.warning(`${ok} uploaded, ${failed} failed. Check the marked photos and press Upload to retry.`);
        } else {
            actionNotifier.error(failed === 1 ? "The photo could not be uploaded." : `${failed} photos could not be uploaded.`);
        }
    };

    const blockOptions = fixedBlock ? [fixedBlock] : blocks;
    const itemLower = item.noun;

    return (
        <div className="ofc-page">
            <div className="ofc-pagehead">
                <div>
                    <span className="ofc-pagehead__eyebrow">{eyebrow}</span>
                    <h1 className="ofc-pagehead__title">{title}</h1>
                    <p className="ofc-pagehead__sub">{subtitle}</p>
                </div>
            </div>

            <div className="ofc-upload">
                <form className="ofc-card" onSubmit={submit}>
                    <div className="ofc-card__body ofc-form">
                        <div className="ofc-form__row">
                            <div className="ofc-form__field">
                                <label className="ofc-label" htmlFor="up-block">
                                    Block <span className="ofc-label__hi">(प्रखंड)</span>
                                </label>
                                <select
                                    id="up-block"
                                    className="ofc-input"
                                    value={block}
                                    onChange={(e) => setBlock(e.target.value)}
                                    disabled={Boolean(fixedBlock) || submitting}
                                    required
                                >
                                    <option value="">{loadingBlocks && !fixedBlock ? "Loading blocks…" : "Select block"}</option>
                                    {blockOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>

                            <div className="ofc-form__field">
                                <label className="ofc-label" htmlFor="up-date">
                                    Photo date <span className="ofc-label__hi">(फोटो की तिथि)</span>
                                </label>
                                <input
                                    id="up-date"
                                    type="date"
                                    className="ofc-input"
                                    value={date}
                                    max={today()}
                                    onChange={(e) => setDate(e.target.value)}
                                    disabled={submitting}
                                    required
                                />
                            </div>
                        </div>

                        <div className="ofc-form__field">
                            <label className="ofc-label" htmlFor="up-item">
                                {item.label} <span className="ofc-label__hi">({item.labelHi})</span>
                            </label>
                            <ReactSelect
                                inputId="up-item"
                                placeholder={
                                    !block ? "Select a block first" : loadingItems ? `Loading ${item.plural}…` : `Search ${itemLower} by name or code`
                                }
                                isLoading={loadingItems}
                                options={items}
                                value={selected}
                                onChange={setSelected}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                noOptionsMessage={() => `No ${item.plural} found`}
                                isDisabled={!block || submitting}
                            />
                            <span className="ofc-form__help">
                                {block && !loadingItems ? `${items.length} ${item.plural} in ${block}` : " "}
                            </span>
                        </div>

                        <div className="ofc-form__field">
                            <span className="ofc-label">
                                Photos {photos.length > 0 && <span className="ofc-label__hi">({photos.length} selected)</span>}
                            </span>
                            <PhotoPicker photos={photos} onAdd={addPhotos} onRemove={removePhoto} disabled={submitting} />
                        </div>
                    </div>

                    <div className="ofc-form__footer">
                        <span className="ofc-form__status">
                            {progress ? `Uploading ${progress.current} of ${progress.total}…` : missing || `Ready to upload ${pending.length} photo${pending.length === 1 ? "" : "s"}`}
                        </span>
                        <button type="submit" className="ofc-btn ofc-btn--primary" disabled={!ready}>
                            <i className={`fa ${submitting ? "fa-spinner fa-spin" : "fa-cloud-upload"}`} />
                            {submitting
                                ? "Uploading…"
                                : pending.length > 1
                                    ? `Upload ${pending.length} photos`
                                    : "Upload photo"}
                        </button>
                    </div>
                </form>

                <UploadGuidelines steps={[
                    `Select the block, then search for the ${itemLower} you inspected.`,
                    "Pick the date the photo was taken.",
                    "Choose one or more photos and check the previews.",
                    "Press Upload. Each photo is saved separately.",
                ]} />
            </div>

            <UploadHistory
                title="My upload history"
                itemLabel={item.label}
                fetchPage={fetchHistory}
                blocks={fixedBlock ? [] : blocks}
                refreshKey={historyKey}
            />
        </div>
    );
};

export default PhotoUploadScreen;
